import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import LineChart from '@components/chart/PercentLineChart'
import Header from '@components/header/Header'
import ConformityCard from '@components/card/RewardConformityCard'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import ChartTag from '@components/chart/ChartTag'
import SortSelector from '@components/modal/SortSelector'
import ButtonSelect from '@components/select/ButtonSelect'
import Picker from '@components/picker/Picker'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as MetierStore from '@store/metier/metier'
import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { format } from 'date-fns'
import { MODES, getAxis, findAxisIndex, findAxisLabels } from './utils'

import Logger from '@utils/logger'

interface Props {
  societeId: string
}

const Presta = ({ societeId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()

  const site = useReducer(SiteStore.store, (s) => s.site)
  const user = useReducer(UserStore.store, (s) => s.user)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const societe = React.useMemo(() => services.find((s) => s.id === societeId), [services, societeId])

  const metiers = useReducer(MetierStore.store, (s) => s.metiers)
  const [societeMetiers, setSocieteMetiers] = React.useState<ControleQualiteMetierResponse[]>([])

  const [stats, setStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [histo, setHisto] = React.useState<ControleQualiteHistoriqueResponse[]>([])
  const [societesHisto, setSocietesHisto] = React.useState<ControleQualiteHistoriqueResponse[]>([])

  const [mode, setMode] = React.useState<'all' | 'year' | 'halfYear' | 'month'>('halfYear')
  const [metier, setMetier] = React.useState<string>()

  const mySociete = !!user && user.societeId === societeId

  const axis = React.useMemo(() => getAxis(mode, [...histo, ...societesHisto]), [mode, histo, societesHisto])

  const labels = React.useMemo(() => findAxisLabels(mode, axis, i18n), [axis])

  const data = React.useMemo(() => {
    const myHisto = {
      items: histo
        .map((h) => ({ value: h.tauxConformiteMoyen, index: findAxisIndex(mode, new Date(h.date), axis) }))
        .filter((item) => item.index >= 0),
      color: theme.colors.primary,
    }

    return mySociete
      ? [myHisto]
      : [
          {
            items: societesHisto
              .map((h) => ({ value: h.tauxConformiteMoyen, index: findAxisIndex(mode, new Date(h.date), axis) }))
              .filter((item) => item.index >= 0),
            color: theme.colors.disable,
          },
          myHisto,
        ]
  }, [histo, societesHisto, axis])

  const params = MODES[mode]

  React.useEffect(() => {
    if (site) {
      const filters = {
        dateDebut: format(params.dateDebut, 'yyyy-MM-dd'),
        dateFin: format(params.dateFin, 'yyyy-MM-dd'),
        intervalle: params.intervalle,
        societeId,
        metierId: metier,
        siteId: site.id,
      }

      // get all controle for this societe and extract audited metiers
      api.controle
        .all({ dateDebut: filters.dateDebut, dateFin: filters.dateFin, societeIds: [societeId], siteIds: [site.id] })
        .then((controles) => {
          const audited = metiers.filter((m) => controles.some((c) => c.metierId === m.id))
          setSocieteMetiers(audited)

          if (!audited.some((m) => m.id === metier)) {
            setMetier(audited[0]?.id)
          }
        })
        .catch((err) => {
          setSocieteMetiers([])
          Logger.error(err)
        })
    }
  }, [params, metiers.length, !!site])

  React.useEffect(() => {
    if (site) {
      // societe stats
      api.controle
        .stats({ societeId, siteId: site.id })
        .then(setStats)
        .catch((err) => {
          setStats(undefined)
          Logger.error(err)
        })

      const filters = {
        dateDebut: format(params.dateDebut, 'yyyy-MM-dd'),
        dateFin: format(params.dateFin, 'yyyy-MM-dd'),
        intervalle: params.intervalle,
        societeId,
        metierId: metier,
        siteId: site.id,
      }

      if (filters.metierId) {
        // current societe scores
        api.controle
          .historique(filters)
          .then((list) => setHisto(list.historique))
          .catch((err) => {
            setHisto([])
            Logger.error(err)
          })

        // other societes scores
        if (!mySociete) {
          api.controle
            .historique({
              ...filters,
              exclureSociete: true,
            })
            .then((list) => setSocietesHisto(list.historique))
            .catch((err) => {
              setSocietesHisto([])
              Logger.error(err)
            })
        }
      }
    }
  }, [mode, metier, mySociete, !!site])

  if (!societe) {
    return (
      <Root header={i18n.t('screens.controles.title')} centered onLeft={navigation.goBack}>
        <NoContent>{i18n.t('screens.controles.form.presta.empty')}</NoContent>
      </Root>
    )
  }

  const headerLinks = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.jumpTo('/controles') },
    { label: societe.nameSociete, onClick: () => null },
  ]

  const detailButton: HeaderButton = {
    label: i18n.t('screens.controleDetail.detail'),
    onClick: () => navigation.push(`/controles/details/detail?societeId=${societeId}`),
    color: 'light',
  }

  const historyButton: HeaderButton = {
    label: i18n.t('screens.controleDetail.history'),
    onClick: () => navigation.push(`/controles/history/list?societeId=${societeId}`),
    color: 'light',
    status: stats && stats.nombreControle > 0 ? 'active' : 'disabled',
  }

  const startControleButton: HeaderButton = {
    label: i18n.t('screens.controles.start'),
    onClick: () => navigation.push(`/controles/form/start?societeId=${societeId}`),
  }

  const options = Object.keys(MODES).map((key) => ({ label: i18n.t(`screens.controleDetail.sort.${key}`), value: key }))
  const option = options.find((opt) => opt.value === mode)

  const metierOptions = societeMetiers.map((m) => ({ label: m.libelle, value: m.id }))
  const metierOption = metierOptions.find((opt) => opt.value === metier)

  const renderInfos = () => {
    if (!stats || !societe) {
      return null
    }

    if (bp === 'phone') {
      return (
        <InfosContainer>
          <InfoLightTitleContainer>
            <Name>{societe.nameSociete}</Name>
            <InfoLight>{i18n.t('screens.controleDetail.countControle', { count: stats.nombreControle })}</InfoLight>
          </InfoLightTitleContainer>

          <InfoLightContainer>
            <InfoLight>{`${stats.nombreControleSansObservation} ${i18n.t(
              'screens.controleDetail.noObservation'
            )}`}</InfoLight>
          </InfoLightContainer>
          <InfoLightContainer>
            <InfoLight>{`${stats.nombreControleAvecObservation} ${i18n.t(
              'screens.controleDetail.observation'
            )}`}</InfoLight>
          </InfoLightContainer>
          <InfoLightContainer>
            <InfoLight>{`${stats.nombreControleNonConforme} ${i18n.t('screens.controleDetail.noConforme', {
              count: stats.nombreControleNonConforme,
            })}`}</InfoLight>
          </InfoLightContainer>

          <RewardContainer>
            <ConformityCard score={stats.tauxConformiteMoyen} />
          </RewardContainer>
        </InfosContainer>
      )
    }

    return (
      <InfosContainer>
        <TotalContainer>
          <TotalCount>{stats.nombreControle}</TotalCount>
          <Total>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</Total>
        </TotalContainer>

        <Infos>
          <InfoCard
            activeOpacity={0.8}
            onPress={() => navigation.push(`/controles/history/list?societeId=${societeId}&observation=false`)}>
            <InfoCount>{stats.nombreControleSansObservation}</InfoCount>
            <Info>{i18n.t('screens.controleDetail.noObservation')}</Info>
          </InfoCard>
          <InfoCard
            activeOpacity={0.8}
            onPress={() => navigation.push(`/controles/history/list?societeId=${societeId}&observation=true`)}>
            <InfoCount>{stats.nombreControleAvecObservation}</InfoCount>
            <Info>{i18n.t('screens.controleDetail.observation')}</Info>
          </InfoCard>
          <InfoCard
            activeOpacity={0.8}
            onPress={() => navigation.push(`/controles/history/list?societeId=${societeId}&conforme=false`)}>
            <InfoCount>{stats.nombreControleNonConforme}</InfoCount>
            <Info>{i18n.t('screens.controleDetail.noConforme', { count: stats.nombreControleNonConforme })}</Info>
          </InfoCard>
        </Infos>

        <ConformityCard score={stats.tauxConformiteMoyen} />
      </InfosContainer>
    )
  }

  return (
    <Root
      header={societe.nameSociete}
      contentContainerStyle={{ flexGrow: 1 }}
      onLeft={navigation.goBack}
      renderSubHeader={renderInfos}>
      {bp !== 'phone' && (
        <Header
          links={headerLinks}
          title={societe.nameSociete}
          buttons={mySociete ? [detailButton, historyButton, startControleButton] : [detailButton, historyButton]}
          renderBottom={renderInfos}
        />
      )}

      <ContentContainer>
        <SectionTitle title={i18n.t('screens.controleDetail.graph')} />

        {bp === 'phone' && (
          <Sort>
            <SortSelector
              sorts={options}
              sort={option}
              reverse
              onSortChange={(opt) => {
                if (opt) {
                  setMode(opt.value)
                }
              }}
            />
          </Sort>
        )}

        <ChartLine>
          <TagContainer>
            <ChartTag label={societe.nameSociete} color={theme.colors.primaryLight} textColor={theme.colors.primary} />
          </TagContainer>
          {!mySociete && (
            <TagContainer>
              <ChartTag
                label={i18n.t('screens.controleDetail.other')}
                color={bp === 'phone' ? theme.colors.background : theme.colors.backgroundSecondary}
                textColor={theme.colors.disable}
              />
            </TagContainer>
          )}

          {bp !== 'phone' && (
            <Sort>
              <SortSelector
                sorts={options}
                sort={option}
                reverse
                onSortChange={(opt) => {
                  if (opt) {
                    setMode(opt.value)
                  }
                }}
              />
            </Sort>
          )}
        </ChartLine>

        <ChartContainer>
          {histo.length === 0 && societesHisto.length === 0 ? (
            <Empty>
              <EmptyText>{i18n.t('screens.controleDetail.empty')}</EmptyText>
            </Empty>
          ) : (
            <LineChart key={width} labels={labels} data={data} size={bp === 'phone' ? 'small' : 'large'} />
          )}

          {metierOptions.length > 0 && (
            <MetierSelect>
              <Picker
                value={metierOption}
                disabled={metierOptions.length === 1}
                options={metierOptions}
                onChange={(opt) => setMetier(opt?.value)}
              />
            </MetierSelect>
          )}
        </ChartContainer>
      </ContentContainer>

      {bp === 'phone' && (
        <Buttons>
          <ButtonSelect label={detailButton.label} onClick={detailButton.onClick} />
          <SelectSeparator />
          <ButtonSelect label={historyButton.label} onClick={historyButton.onClick} />
          {mySociete && (
            <>
              <SelectSeparator />
              <ButtonSelect label={startControleButton.label} onClick={startControleButton.onClick} />
            </>
          )}
        </Buttons>
      )}
    </Root>
  )
}

export default Presta

const InfosContainer = styled(View)`
  padding: 32px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-direction: row; flex-wrap: wrap'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 0px; padding-top: 24px'};
`

const TotalCount = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Total = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
`

const TotalContainer = styled(View)`
  margin-right: 24px;
`

const Infos = styled(View)`
  flex-direction: row;
  flex-grow: 1;
  margin: 8px 0;
`

const InfoCard = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.cardSecondary};
  padding: 16px;
  align-items: center;
  flex: 1;
  margin-right: 16px;
  max-width: 250px;
  min-width: 100px;
`

const InfoCount = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  font-size: 24px;
  line-height: 28px;
  margin-bottom: 8px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const ContentContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-grow: 1'};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`

const ChartContainer = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.cardSecondary};
  padding: 5px;
  border-radius: 8px;
  ${(props) => (props.theme.breakpoint === 'phone' ? 'height: 300px' : 'flex: 1; min-height: 400px')};
  padding-top: 55px;
  margin-bottom: 16px;
`

const ChartLine = styled(View)`
  flex-flow: row wrap;
  align-items: center;
  margin: 16px 0;
`

const TagContainer = styled(View)`
  margin-right: 16px;
`

const Sort = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'position: absolute; right: 16px; top: 18px;' : 'flex: 1')};
  align-items: flex-end;
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  color: ${(props) => props.theme.colors.linkDisabled};
`

const InfoLight = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.background};
`

const InfoLightContainer = styled(View)`
  background-color: ${(props) => `${props.theme.colors.cardSecondary}22`};
  padding: 8px;
  margin-bottom: 1px;
`

const RewardContainer = styled(View)`
  position: absolute;
  bottom: 0;
  right: 0;
`

const InfoLightTitleContainer = styled(View)`
  margin-bottom: 8px;
`

const Buttons = styled(View)`
  padding: 16px 0;
`

const SelectSeparator = styled(View)`
  height: 2px;
`

const MetierSelect = styled(View)`
  position: absolute;
  right: 8px;
  top: 8px;
  width: 200px;
`

const Empty = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const EmptyText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
  margin: 0 24px;
  margin-bottom: 50px;
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`
